import {
  ClockCircleOutlined,
  DownOutlined,
  PlusOutlined,
  StopOutlined,
  UpOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Select } from "antd";
import { Modal } from "antd";
import { Popconfirm } from "antd";
import { Alert } from "antd";
import { Button, Collapse, List, message, Tag } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { VerwijderButtonConfirm } from "~/components/buttons/VerwijderButtonConfirm.jsx";
import { $t } from "~/i18n.js";
import { aanmeldingenActions } from "~/redux/actions.js";

const VerwijderRegistrationButton = ({ onConfirm, loading }) => {
  return (
    <Popconfirm
      key="remove"
      placement="bottom"
      title={$t("Zeker weten?")}
      onConfirm={onConfirm}
      okText={$t("Ja")}
      cancelText={$t("Nee")}
    >
      <Button
        key="delete"
        danger
        shape="circle"
        size="small"
        loading={loading}
        icon={<StopOutlined />}
      />
    </Popconfirm>
  );
};

const SignupModal = ({ open, onClose, onConfirm, users, list, isFull }) => {
  if (!open) return null;

  const [selectedUser, setSelectedUser] = useState(null);
  useEffect(() => {
    setSelectedUser(null);
  }, [open]);

  return (
    <Modal
      title={$t("Meld lid aan")}
      open={open}
      onCancel={onClose}
      onOk={onConfirm}
      okText={$t("Bevestig")}
      cancelText={$t("Annuleren")}
      okButtonProps={{
        disabled: !selectedUser,
        onClick: () => onConfirm(selectedUser),
      }}
    >
      <p>
        <span className="bold label">{$t("Lijst")}</span> {list.name}
      </p>

      <div>
        <span className="bold label">{$t("Gebruiker")}</span>

        <Select
          showSearch
          filterOption={(input, option) =>
            option &&
            option.children &&
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          placeholder={$t("Selecteer lid om aan te melden")}
          value={selectedUser}
          onChange={setSelectedUser}
          style={{ marginLeft: 6 }}
        >
          {users.map(
            (vlieger, key) =>
              vlieger.id > 0 && (
                <Select.Option
                  value={vlieger.id}
                  key={key}
                  disabled={
                    list.registrations.some((r) => r.user_id == vlieger.id) ||
                    !list.groups.some((g) => vlieger.group_keys?.includes(g))
                  }
                >
                  {vlieger.name}
                </Select.Option>
              )
          )}
        </Select>
      </div>

      {isFull && (
        <Alert
          style={{ marginTop: 12 }}
          message={$t(
            "De lijst is vol, nieuwe aanmeldingen komen op de wachtlijst."
          )}
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
};

export const RegistrationList = ({
  aangemeldeClusters,
  list,
  user,
  saveRegistration,
  users,
  n_instructors,
  openRegistration,
  readOnly
}) => {
  const [loading, setLoading] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  if (!list) return null;

  const mainList = list.registrations.filter((r) => !r.is_waitlist);
  const waitingList = list.registrations.filter((r) => r.is_waitlist);

  let maxLength = list.registration_list_length;
  if (list.list_length_type === "per_instructor") {
    maxLength = n_instructors * list.registration_list_length;
  }
  const isFull = mainList.length >= maxLength;
  const magVerwijderen = list.registrations.some((r) => r.user_id === user?.id);

  const canSignup =
    !magVerwijderen && list.groups.some((g) => user?.group_keys?.includes(g));

  // if cluster, only one signup per cluster allowed
  const disabledSignup = list.cluster && aangemeldeClusters[list.cluster];

  // permission to alter registration list
  const canSignupOthers = user?.iedereen_aanmelden;

  const triggerOpenPilot = (registration) => {
    setOpenPilot(registration.user_id);
  }

  const signUp = async () => {
    setLoading(true);
    const data = await saveRegistration({
      dag_id: list.day_id,
      registration_list_id: list.id,
      action: isFull ? "add_to_waitinglist" : "register",
    });
    setLoading(false);

    if (data?.not_registered) {
      Modal.error({
        title: $t("Maximum aantal aanmeldingen bereikt"),
        content: (
          <div>
            <p>
              {$t(
                "Om te zorgen dat iedereen een eerlijke kans krijgt om deel te nemen, is er een maximum aantal aanmeldingen ingesteld."
              )}
            </p>

            <p style={{ margin: "6px 0" }}>
              {$t("Toegestaan: ${max} aanmeldingen in de toekomst", {
                max: data?.max,
              })}
            </p>

            <p>
              {$t(
                "Wil je op deze dag aanmelden, dan moet je op een andere dag afmelden of wachten."
              )}
            </p>
          </div>
        ),
      });
    }
  };

  const remove = async () => {
    setLoading(true);
    await saveRegistration({
      dag_id: list.day_id,
      registration_list_id: list.id,
      action: "remove",
    });
    setLoading(false);
  };

  const registrationAction = async (registration_id, action) => {
    setLoading(true);
    await saveRegistration({
      dag_id: list.day_id,
      registration_list_id: list.id,
      action: action,
      registration_id: registration_id,
    });
    setLoading(false);
  };

  return (
    <div style={{ maxWidth: 400, marginBottom: 12 }}>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <p className="small">
          {list.name}{" "}
          <Tag
            style={{ fontSize: 11, padding: 2, lineHeight: 1.1 }}
            color={isFull ? "red" : "green"}
          >
            <UserOutlined /> {mainList.length} / {maxLength}
          </Tag>
        </p>

        {canSignup && !isFull && (
          <Button
            loading={loading}
            size="small"
            onClick={signUp}
            icon={<PlusOutlined />}
            disabled={disabledSignup || readOnly}
          >
            {$t("Meld aan")}
          </Button>
        )}

        {canSignup && isFull && list.has_waitinglist && (
          <Button
            loading={loading}
            size="small"
            onClick={signUp}
            icon={<PlusOutlined />}
            disabled={disabledSignup || readOnly}
            type="dashed"
          >
            {$t("Wachtlijst")}
          </Button>
        )}

        {magVerwijderen && (
          <VerwijderButtonConfirm
            size="small"
            onConfirm={remove}
            loading={loading}
            disabled={readOnly}
          />
        )}

        {canSignupOthers && (
          <Button
            type="dashed"
            size="small"
            onClick={() => setShowSignupModal(true)}
            icon={<PlusOutlined />}
            disabled={disabledSignup}
          >
            {$t("Lid")}
          </Button>
        )}

        <SignupModal
          open={showSignupModal}
          onClose={() => setShowSignupModal(false)}
          users={users}
          list={list}
          isFull={isFull && list.has_waitinglist}
          onConfirm={async (user_id) => {
            setShowSignupModal(false);
            setLoading(true);
            await saveRegistration({
              user_id,
              dag_id: list.day_id,
              registration_list_id: list.id,
              action:
                isFull && list.has_waitinglist
                  ? "add_to_waitinglist"
                  : "register",
            });
            setLoading(false);
          }}
        />
      </div>

      {/* Main Registration List */}
      {mainList.length === 0 && (
        <p className="small gray" style={{ marginTop: 4 }}>
          {$t("Geen aanmeldingen.")}
        </p>
      )}
      {mainList.length > 0 && (
        <List
          size="small"
          dataSource={mainList}
          renderItem={(registration) => (
            <List.Item
              extra={
                canSignupOthers && (
                  <div className="row" style={{ gap: 6 }}>
                    {list.has_waitinglist && (
                      <Button
                        shape="circle"
                        size="small"
                        onClick={() =>
                          registrationAction(registration.id, "demote")
                        }
                        loading={loading}
                        icon={<DownOutlined />}
                      />
                    )}
                    <VerwijderRegistrationButton
                      loading={loading}
                      onConfirm={() =>
                        registrationAction(registration.id, "delete")
                      }
                    />
                  </div>
                )
              }
            >
              <div className="clickable" onClick={() => openRegistration(registration)}>{registration.user_name}</div>
            </List.Item>
          )}
        />
      )}

      {/* Waiting List */}
      {waitingList.length > 0 && (
        <>
          <Collapse
            ghost
            size="small"
            className="noPadding"
            items={[
              {
                key: "waitinglist",
                label: (
                  <span className="small">
                    {$t("Wachtlijst")}{" "}
                    <Tag style={{ fontSize: 11, padding: 2, lineHeight: 1.1 }}>
                      <ClockCircleOutlined /> {waitingList.length}
                    </Tag>
                  </span>
                ),
                children: (
                  <List
                    size="small"
                    dataSource={waitingList}
                    renderItem={(registration) => (
                      <List.Item
                        key={registration.id}
                        extra={
                          canSignupOthers && (
                            <div className="row" style={{ gap: 6 }}>
                              <Button
                                shape="circle"
                                size="small"
                                onClick={() =>
                                  registrationAction(registration.id, "promote")
                                }
                                loading={loading}
                                icon={<UpOutlined />}
                              />
                              <VerwijderRegistrationButton
                                loading={loading}
                                onConfirm={() =>
                                  registrationAction(registration.id, "delete")
                                }
                              />
                            </div>
                          )
                        }
                      >
                        <div className="clickable" onClick={() => openRegistration(registration)}>{registration.user_name}</div>
                      </List.Item>
                    )}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </div>
  );
};

export const CardRegistrationList = ({
  aanmeldingen,
  registration_lists,
  currentUser,
  n_instructors,
  setOpenPilot,
  currentAanmelding
}) => {
  if (!registration_lists || registration_lists.length === 0) {
    return <div>{$t("Er zijn geen registratielijsten voor deze dag.")}</div>;
  }

  const readOnly = !currentAanmelding || !currentAanmelding.aangemeld

  const dispatch = useDispatch();

  const saveRegistration = (data) =>
    dispatch(aanmeldingenActions.saveRegistration(data));

  let aangemeldeClusters = {};
  registration_lists.forEach((r) => {
    if (!r.cluster) return;
    r.registrations.some((reg) => {
      if (!reg.is_waitlist && reg.user_id === currentUser?.id) {
        aangemeldeClusters[r.cluster] = true;
        return true;
      }
    });
  });

  const users = aanmeldingen ? aanmeldingen.filter(a => a.aangemeld).map((a) => a.vlieger) : [];

  const triggerOpenPilot = (registration) => {
    try {
      setOpenPilot(aanmeldingen.find((a) => a?.vlieger?.id === registration.user_id)?.vlieger);
    } catch (error) {
      console.error("Error in triggerOpenPilot:", error);
    }
  }

  return (
    <>
      {registration_lists.map((list) => (
        <RegistrationList
          openRegistration={triggerOpenPilot}
          n_instructors={n_instructors}
          aangemeldeClusters={aangemeldeClusters}
          key={list.id}
          list={list}
          user={currentUser}
          saveRegistration={saveRegistration}
          users={users}
          readOnly={readOnly}
        />
      ))}
    </>
  );
};
